/**
 * Componente para proteger rutas basado en permisos
 */

import { Navigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import type { Module, Action } from "@/lib/permissions";

interface RequirePermissionProps {
  module: Module;
  action?: Action;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * Componente que protege contenido basado en permisos
 * Si el usuario no tiene permiso, muestra el fallback o redirige
 */
export function RequirePermission({
  module,
  action = "view",
  children,
  fallback,
  redirectTo,
}: RequirePermissionProps) {
  const { can } = usePermissions();

  if (!can(module, action)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    // Fallback por defecto: mensaje de acceso denegado
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center mb-4 shadow-lg shadow-red-500/30">
          <svg
            className="w-8 h-8 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Acceso Denegado
        </h2>
        <p className="text-gray-500 max-w-md">
          No tienes permisos para acceder a esta sección. Contacta a tu
          administrador si crees que esto es un error.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * HOC para proteger una página completa
 */
export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  module: Module,
  action: Action = "view",
) {
  return function ProtectedComponent(props: P) {
    return (
      <RequirePermission module={module} action={action}>
        <WrappedComponent {...props} />
      </RequirePermission>
    );
  };
}

/**
 * Hook para mostrar/ocultar elementos según permisos
 * Retorna null si no tiene permiso, children si lo tiene
 */
interface ShowIfAllowedProps {
  module: Module;
  action?: Action;
  children: React.ReactNode;
}

export function ShowIfAllowed({
  module,
  action = "view",
  children,
}: ShowIfAllowedProps) {
  const { can } = usePermissions();

  if (!can(module, action)) {
    return null;
  }

  return <>{children}</>;
}

/**
 * Componente para mostrar contenido solo a admins
 */
interface AdminOnlyProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function AdminOnly({ children, fallback = null }: AdminOnlyProps) {
  const { isAdmin } = usePermissions();

  if (!isAdmin) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Componente para mostrar contenido solo a directores de club
 */
interface ClubDirectorOnlyProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function ClubDirectorOnly({
  children,
  fallback = null,
}: ClubDirectorOnlyProps) {
  const { isClubDirector } = usePermissions();

  if (!isClubDirector) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
